import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TablePagination,
  Grid,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Avatar,
  Badge,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Menu,
  ListItemIcon,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import AlertModal from '../../components/AlertModal';
import {
  Visibility,
  Cancel,
  Search,
  Receipt,
  CheckCircle,
  Home,
  Business,
  Person,
  CalendarToday,
  FilterList,
  Download,
  Refresh,
  Print,
  AccountBalance,
  Description,
  Comment,
  MoneyOff,
  Timeline,
  Delete,
  CheckCircleOutline,
  WatchLater,
  ThumbUp,
  ThumbDown,
  PictureAsPdf,
  Close,
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { getBookings, updateBookingStatus, getProjects } from '../../services/project';
import { getAssociates } from '../../services/associate';
import Loader from '../../themes/useLoader';
import { hasWritePermission, hasReadPermission } from '../../utils/menuFilter';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import ExportButton from '../../components/ExportButton/ExportButton';
import { exportAsPDF as exportPDFUtil, exportAsExcel } from '../../utils/importExport';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import MoreFilters from '../../components/MoreFilters/MoreFilters';


const BookingApproval = () => {
  const canWriteBookingApproval = hasWritePermission('booking.booking-approval') || hasWritePermission('plot-booking.booking-approval');
  const canReadBookingApproval = hasReadPermission('booking.booking-approval') || hasReadPermission('plot-booking.booking-approval');
  const [alertModal, setAlertModal] = useState({ open: false, title: 'Alert', message: '' });

  const showAlert = (message, title = 'Alert') => {
    setAlertModal({ open: true, title, message });
  };

  const closeAlert = () => {
    setAlertModal({ open: false, title: 'Alert', message: '' });
  };
  const history = useHistory();
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [paymentPlanFilter, setPaymentPlanFilter] = useState('all');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const exportMenuOpen = Boolean(exportAnchorEl);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bookingData, setBookingData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalRemark, setApprovalRemark] = useState('');
  const [approvalDate, setApprovalDate] = useState(
    moment().format('YYYY-MM-DD')
  );
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [actionType, setActionType] = useState('approve'); // 'approve' or 'reject'

  // Advanced Filters State
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [paymentPlansList, setPaymentPlansList] = useState([]);
  const [advancedFilters, setAdvancedFilters] = useState({
    fromDate: null,
    toDate: null,
    inventoryType: 'all',
    associateId: null
  });
  const [tempAdvancedFilters, setTempAdvancedFilters] = useState({
    fromDate: null,
    toDate: null,
    inventoryType: 'all',
    associateId: null
  });
  const [selectedFilterAssociate, setSelectedFilterAssociate] = useState(null);
  const [tempSelectedFilterAssociate, setTempSelectedFilterAssociate] = useState(null);
  const [sortBy, setSortBy] = useState('');
  const [tempSortBy, setTempSortBy] = useState('');
  const [projectsList, setProjectsList] = useState([]);
  const [associatesList, setAssociatesList] = useState([]);
  const [filterDataLoading, setFilterDataLoading] = useState(false);

  // Fetch Projects and Associates for filters
  useEffect(() => {
    const loadFilterData = async () => {
      setFilterDataLoading(true);
      try {
        const [projRes, assocRes] = await Promise.all([
          getProjects(),
          getAssociates({ limit: 1000 })
        ]);
        setProjectsList(projRes || []);
        
        // Handle different associate response structures
        let assocData = [];
        if (Array.isArray(assocRes)) assocData = assocRes;
        else if (assocRes?.data) assocData = Array.isArray(assocRes.data) ? assocRes.data : (assocRes.data.docs || []);
        
        setAssociatesList(assocData);

        // Fetch payment plans details from global master details
        try {
          const base = process.env.REACT_APP_API_BASE_URL;
          const planRes = await axios.get(`${base}/api/globalMasterDetails?type=paymentplan`);
          if (planRes.data?.success && Array.isArray(planRes.data.data)) {
            setPaymentPlansList(planRes.data.data);
          }
        } catch (planErr) {
          console.error('Failed to load payment plan details:', planErr);
        }
      } catch (err) {
        console.error('Failed to load filter data:', err);
      } finally {
        setFilterDataLoading(false);
      }
    };
    loadFilterData();
  }, []);

  const handleOpenFilterDialog = () => setMoreFiltersOpen(true);
  const handleCloseFilterDialog = () => setMoreFiltersOpen(false);

  const handleApplyFilters = () => {
    setSortBy(tempSortBy);
    setAdvancedFilters(tempAdvancedFilters);
    setSelectedFilterAssociate(tempSelectedFilterAssociate);
    setPage(0);
    handleCloseFilterDialog();
  };

  const handleResetFilters = () => {
    setSortBy('');
    setTempSortBy('');
    setAdvancedFilters({
      fromDate: null,
      toDate: null,
      inventoryType: 'all',
      associateId: null
    });
    setTempAdvancedFilters({
      fromDate: null,
      toDate: null,
      inventoryType: 'all',
      associateId: null
    });
    setSelectedFilterAssociate(null);
    setTempSelectedFilterAssociate(null);
    setPage(0);
  };

  const handleClearFilters = () => {
    handleResetFilters();
  };

  const handleResetAll = () => {
    setSearchQuery('');
    setPaymentPlanFilter('all');
    handleResetFilters();
  };

  const removeFilter = (key) => {
    if (key === 'associateId') {
      setSelectedFilterAssociate(null);
      setTempSelectedFilterAssociate(null);
      setAdvancedFilters(prev => ({ ...prev, associateId: null }));
      setTempAdvancedFilters(prev => ({ ...prev, associateId: null }));
    } else if (key === 'fromDate' || key === 'toDate') {
      setAdvancedFilters(prev => ({ ...prev, [key]: null }));
      setTempAdvancedFilters(prev => ({ ...prev, [key]: null }));
    } else {
      setAdvancedFilters(prev => ({ ...prev, [key]: 'all' }));
      setTempAdvancedFilters(prev => ({ ...prev, [key]: 'all' }));
    }
  };

  const handleFilterKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleApplyFilters();
      setMoreFiltersOpen(false);
    }
  };

  const handleOpenExportMenu = (event) => setExportAnchorEl(event.currentTarget);
  const handleCloseExportMenu = () => setExportAnchorEl(null);

  const getExportRows = () => {
    const rows = getFilteredByStatus();
    return rows.map(b => ({
      bookingId: b.bookingId,
      plotBookingDate: b.plotBookingDate,
      plotNo: b.plotNo,
      branchName: b.branchName,
      paymentPlan: b.paymentPlan,
      customer: b.customer,
      associate: b.associate,
      plotAmount: b.plotAmount,
      discountAmount: b.discountAmount,
      paidAmount: b.paidAmount,
      status: b.status,
    }));
  };

  const exportAsCSV = () => {
    const rows = getExportRows();
    const headers = ['Booking ID', 'Date', 'Plot No', 'Branch', 'Payment Plan', 'Customer', 'Associate', 'Plot Amount', 'Discount Amount', 'Paid Amount', 'Status'];
    const escapeCSV = (val) => {
      const s = String(val ?? '');
      if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
      return s;
    };
    const csvLines = [headers.join(',')].concat(
      rows.map(r => [
        escapeCSV(r.bookingId),
        escapeCSV(r.plotBookingDate),
        escapeCSV(r.plotNo),
        escapeCSV(r.branchName),
        escapeCSV(r.paymentPlan),
        escapeCSV(r.customer),
        escapeCSV(r.associate),
        r.plotAmount,
        r.discountAmount,
        r.paidAmount,
        escapeCSV(r.status)
      ].join(','))
    );
    const blob = new Blob(["\uFEFF" + csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `booking_approvals_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportAsPDF = () => {
    const rows = getExportRows();
    exportPDFUtil({
      rows: rows.map(r => ({
        'Booking ID': r.bookingId,
        'Date': r.plotBookingDate,
        'Plot No': r.plotNo,
        'Branch': r.branchName,
        'Payment Plan': r.paymentPlan,
        'Customer': r.customer,
        'Associate': r.associate,
        'Property Amount': r.plotAmount,
        'Discount': r.discountAmount,
        'Paid Amount': r.paidAmount,
        'Status': r.status,
      })),
      title: 'Booking Approval Report',
      fileNameBase: 'booking_approvals',
    });
  };

  const exportAsExcelHandler = () => {
    const rows = getExportRows();
    exportAsExcel({
      rows: rows.map(r => ({
        'Booking ID': r.bookingId,
        'Date': r.plotBookingDate,
        'Plot No': r.plotNo,
        'Branch': r.branchName,
        'Payment Plan': r.paymentPlan,
        'Customer': r.customer,
        'Associate': r.associate,
        'Property Amount': r.plotAmount,
        'Discount Amount': r.discountAmount,
        'Paid Amount': r.paidAmount,
        'Status': r.status,
      })),
      sheetName: 'Booking Approvals',
      fileNameBase: 'booking_approvals',
    });
  };

  const handleExport = (type) => {
    handleCloseExportMenu();
    if (type === 'pdf') exportAsPDF();
    if (type === 'csv') exportAsCSV();
  };

  // Map API response to component format
  const mapBookingData = (apiBooking) => {
    // Format booking date
    const bookingDate = apiBooking.bookingDate
      ? new Date(apiBooking.bookingDate).toISOString().split('T')[0]
      : '';

    // Format request date
    const requestDate = apiBooking.createdAt
      ? new Date(apiBooking.createdAt).toLocaleDateString('en-GB')
      : '';

    // Map status from API to component format
    const statusMap = {
      'booked': 'pending',
      'approved': 'approved',
      'rejected': 'rejected',
      'pending': 'pending',
    };

    return {
      id: apiBooking._id || apiBooking.id,
      bookingId: apiBooking.bookingId ? String(apiBooking.bookingId) : '',
      plotBookingDate: bookingDate,
      plotNo: apiBooking.particular?.inventoryDetail || apiBooking.plotNo || '',
      inventoryType: apiBooking.particular?.inventoryType || '',
      branchName: apiBooking.particular?.projectName || apiBooking.branchName || '',
      paymentPlan: apiBooking.paymentPlan || '',
      customer: apiBooking.customer?.name || apiBooking.customer || '',
      associate: apiBooking.associate?.name || apiBooking.associate || '',
      plotAmount: apiBooking.propertyAmount || apiBooking.plotAmount || 0,
      discountAmount: apiBooking.discount || apiBooking.discountAmount || 0,
      authorityCharge: apiBooking.authorityCharge || 0,
      paidAmount: apiBooking.totalReceived || apiBooking.paidAmount || 0,
      status: statusMap[apiBooking.approvedStatus] || apiBooking.approvedStatus || 'pending',
      requestedBy: apiBooking.createdBy || apiBooking.requestedBy || 'Admin User',
      requestDate: requestDate,
      approvedBy: apiBooking.approvedBy || '',
      approvedDate: apiBooking.approvedDate || '',
      rejectedBy: apiBooking.rejectedBy || '',
      rejectedDate: apiBooking.rejectedDate || '',
      rejectionReason: apiBooking.rejectionReason || '',
      // Include original data for view dialog
      _original: apiBooking,
    };
  };

  // Fetch bookings from API
  const fetchBookings = useCallback(async () => {
    console.log('Setting loading to true');
    setLoading(true);
    setError('');
    try {
      const params = {
        approvedStatus: 'pending', // Default to 'booked' status as per API requirement
        page: page + 1, // API uses 1-based pagination
        limit: rowsPerPage,
      };

      // Add payment plan filter if not 'all'
      if (paymentPlanFilter !== 'all') {
        params.paymentPlan = paymentPlanFilter;
      }

      // Add search query if provided
      if (searchQuery) {
        params.search = searchQuery;
      }

      // Add advanced filters
      if (advancedFilters.fromDate) params.fromDate = advancedFilters.fromDate;
      if (advancedFilters.toDate) params.toDate = advancedFilters.toDate;
      if (advancedFilters.inventoryType !== 'all') params.inventoryType = advancedFilters.inventoryType;
      if (advancedFilters.associateId) params.associateId = advancedFilters.associateId;

      if (sortBy) {
        let apiSortBy = '';
        let apiSortOrder = 'asc';

        if (sortBy === 'dateDesc') {
          apiSortBy = 'date';
          apiSortOrder = 'desc';
        } else if (sortBy === 'dateAsc') {
          apiSortBy = 'date';
          apiSortOrder = 'asc';
        }

        if (apiSortBy) {
          params.sortBy = apiSortBy;
          params.sortOrder = apiSortOrder;
        }
      }

      const response = await getBookings(params);

      // Handle different response structures
      let bookings = [];
      let total = 0;

      if (response.success || response.status === true) {
        bookings = Array.isArray(response.data) ? response.data : (response.data?.docs || []);
        total = response.data?.totalDocs || response.data?.total || response.count || bookings.length;
      } else if (Array.isArray(response)) {
        bookings = response;
        total = response.length;
      } else if (response.data) {
        bookings = Array.isArray(response.data) ? response.data : (response.data?.docs || []);
        total = response.data?.totalDocs || response.data?.total || response.count || bookings.length;
      }

      const mappedData = bookings.map(mapBookingData);
      setBookingData(mappedData);
      setTotalCount(total);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError(err.message || 'Failed to load bookings');
      setBookingData([]);
      setTotalCount(0);
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  }, [page, rowsPerPage, paymentPlanFilter, searchQuery, advancedFilters, sortBy]);

  // Fetch bookings on mount and when filters change
  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  // Filter by payment plan only (tabs removed) - now handled by API
  const getFilteredByStatus = () => {
    // API already filters by paymentPlan, so just return the data
    // Additional client-side filtering can be added here if needed
    return bookingData;
  };

  const handleView = (booking) => {
    setSelectedBooking(booking);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedBooking(null);
  };

  const handleApprove = (booking) => {
    setSelectedBooking(booking);
    setApprovalRemark('');
    setActionType('approve');
    setApprovalDialogOpen(true);
  };

  const handleReject = (booking) => {
    setSelectedBooking(booking);
    setApprovalRemark('');
    setActionType('reject');
    setApprovalDialogOpen(true);
  };

  const handleCloseApprovalDialog = () => {
    setApprovalDialogOpen(false);
    setApprovalRemark('');
    setSelectedBooking(null);
    setActionType('approve');
  };

  const handleSubmitApproval = async () => {
    if (!selectedBooking) return;

    setApprovalLoading(true);
    setError('');

    try {
      // Get current user info
      let currentUserName = 'Admin User';
      try {
        const user = localStorage.getItem('user');
        if (user) {
          const userData = JSON.parse(user);
          currentUserName = userData?.user?.name || userData?.name || userData?.username || 'Admin User';
        }
      } catch (err) {
        console.error('Error getting user info:', err);
      }

      // Get booking ID from original data or id field
      const bookingId = selectedBooking._original?._id || selectedBooking.id;
      if (!bookingId) {
        throw new Error('Booking ID not found');
      }

      // Prepare payload for API
      const payload = {
        approvedStatus: actionType === 'approve' ? 1 : 0, // 1 for booked/approved, 0 for cancelled/rejected
        createdBy: currentUserName,
      };

      // Add approvalRemark only if provided
      if (approvalRemark.trim()) {
        payload.approvalRemark = approvalRemark.trim();
        payload.otherRemark = approvalRemark.trim();
      }

      payload.approvalDate = approvalDate || moment().format('YYYY-MM-DD');

      // Call API to update booking status
      const response = await updateBookingStatus(bookingId, payload);

      // Check if request was successful
      if (response.success || response.status === true || response._id) {
        // Show success message
        setError(''); // Clear any previous errors
        const actionText = actionType === 'approve' ? 'approved' : 'rejected';
        showAlert(`Booking ${selectedBooking.bookingId} ${actionText} successfully!`);

        // Close dialog and refresh data
        handleCloseApprovalDialog();
        fetchBookings();
      } else {
        throw new Error(response.message || `Failed to ${actionType} booking`);
      }
    } catch (err) {
      console.error(`Error ${actionType}ing booking:`, err);
      setError(err.message || `Failed to ${actionType} booking. Please try again.`);
    } finally {
      setApprovalLoading(false);
    }
  };


  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
  };

  // Search and filtering are now handled by API
  const displayData = getFilteredByStatus();
  const pendingCount = bookingData.filter(booking => booking.status === 'pending').length;
  const approvedCount = bookingData.filter(booking => booking.status === 'approved').length;
  const rejectedCount = bookingData.filter(booking => booking.status === 'rejected').length;

  // Pagination is now handled by API, so use the data directly
  const paginatedData = displayData;

  const getPaymentProgress = (paid, total) => {
    return (paid / total) * 100;
  };

  if (!canReadBookingApproval) {
    return (
      <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '70vh' }}>
        <Paper elevation={3} sx={{ p: 5, textAlign: 'center', borderRadius: 4, maxWidth: 500 }}>
          <Avatar sx={{ bgcolor: '#fee2e2', color: '#ef4444', width: 80, height: 80, mb: 3, mx: 'auto' }}>
            <Close sx={{ fontSize: 50 }} />
          </Avatar>
          <Typography variant="h4" fontWeight="bold" color="#1a202c" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
            You do not have permission to view Booking Approvals. Please contact your administrator.
          </Typography>
          <Button
            variant="contained"
            onClick={() => history.push('/app/dashboard')}
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3d5afe' },
              borderRadius: 2,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 'bold'
            }}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      {/* <Loader open={loading} message="Loading bookings..." /> */}
      {/* Modern Header Section */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 4, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 56, height: 56, bgcolor: '#536DFE' }}>
            <CheckCircle sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight="bold" color="primary">
              Booking Approval Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review and approve bookings • {displayData.length} records
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, width: { xs: '100%', md: 'auto' }, justifyContent: { xs: 'flex-start', md: 'flex-end' }, alignItems: 'center' }}>
          {canWriteBookingApproval && (
            <ExportButton
              onExportPdf={exportAsPDF}
              onExportCsv={exportAsCSV}
              onExportExcel={exportAsExcelHandler}
              variant="contained"
            />
          )}
          <RefreshButton onClick={fetchBookings} loading={loading} />
        </Box>
      </Box>


      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Search and Filters Section */}
      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 3, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={6}>
            <TextField
              fullWidth
              placeholder="Search by booking ID, customer, plot, agent..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(0); // Reset to first page when search changes
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="primary" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Payment Plan</InputLabel>
              <Select
                value={paymentPlanFilter}
                onChange={(e) => {
                  setPaymentPlanFilter(e.target.value);
                  setPage(0); // Reset to first page when filter changes
                }}
                label="Payment Plan"
              >
                <MenuItem value="all">All Payment Plans</MenuItem>
                {paymentPlansList.map((plan) => (
                  <MenuItem key={plan._id} value={plan.details?.planName || plan.globalMasterName}>
                    {plan.details?.planName || plan.globalMasterName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  flex: 1,
                  height: '56px',
                }}
                onClick={handleResetAll}
                color="inherit"
              >
                Reset
              </Button>
              <Box sx={{ flex: 1 }}>
                <MoreFilters
                  expanded={moreFiltersOpen}
                  onToggle={() => setMoreFiltersOpen(!moreFiltersOpen)}
                  sortBy={tempSortBy}
                  onChangeSortBy={setTempSortBy}
                  onApply={handleApplyFilters}
                  onReset={handleResetFilters}
                  type="project"
                >
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Filter Options</Typography>
                    </Grid>
                    
                    {/* Inventory Type */}
                    <Grid item xs={12}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Inventory Type</InputLabel>
                        <Select
                          value={tempAdvancedFilters.inventoryType}
                          label="Inventory Type"
                          onChange={(e) => setTempAdvancedFilters(prev => ({ ...prev, inventoryType: e.target.value }))}
                        >
                          <MenuItem value="all">All Types</MenuItem>
                          <MenuItem value="Plot">Plot</MenuItem>
                          <MenuItem value="Flat">Flat</MenuItem>
                          <MenuItem value="Villa">Villa</MenuItem>
                          <MenuItem value="Commercial">Commercial</MenuItem>
                          <MenuItem value="Row House">Row House</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Date Range */}
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label='From Date'
                        type='date'
                        InputLabelProps={{ shrink: true }}
                        value={tempAdvancedFilters.fromDate || ''}
                        onChange={(e) => setTempAdvancedFilters(prev => ({ ...prev, fromDate: e.target.value || null }))}
                        onKeyDown={handleFilterKeyDown}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label='To Date'
                        type='date'
                        InputLabelProps={{ shrink: true }}
                        value={tempAdvancedFilters.toDate || ''}
                        onChange={(e) => setTempAdvancedFilters(prev => ({ ...prev, toDate: e.target.value || null }))}
                        onKeyDown={handleFilterKeyDown}
                      />
                    </Grid>

                    {/* Associate */}
                    <Grid item xs={12}>
                      <Autocomplete
                        fullWidth
                        size="small"
                        options={associatesList}
                        loading={filterDataLoading}
                        getOptionLabel={(option) => option.name || ''}
                        value={tempSelectedFilterAssociate}
                        onChange={(_, newValue) => {
                          setTempSelectedFilterAssociate(newValue);
                          setTempAdvancedFilters(prev => ({ ...prev, associateId: newValue ? (newValue._id || newValue.id) : null }));
                        }}
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            label="Associate Name" 
                            placeholder="Search by associate name..."
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {filterDataLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props} key={option._id || option.id}>
                            <Box>
                              <Typography variant="body2">{option.name}</Typography>
                              <Typography variant="caption" color="text.secondary">{option.associateId || option.code}</Typography>
                            </Box>
                          </Box>
                        )}
                      />
                    </Grid>
                  </Grid>
                </MoreFilters>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Active Filter Chips */}
        {Object.values(advancedFilters).some(v => v !== 'all' && v !== null) && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2, pt: 2, borderTop: '1px solid #f0f0f0' }}>
            <Typography variant="caption" sx={{ alignSelf: 'center', mr: 1, fontWeight: 700, color: 'text.secondary' }}>
              ACTIVE FILTERS:
            </Typography>
            {advancedFilters.fromDate && (
              <Chip 
                label={`From: ${moment(advancedFilters.fromDate).format('DD/MM/YYYY')}`} 
                size="small" 
                onDelete={() => removeFilter('fromDate')} 
                color="primary" variant="outlined" 
              />
            )}
            {advancedFilters.toDate && (
              <Chip 
                label={`To: ${moment(advancedFilters.toDate).format('DD/MM/YYYY')}`} 
                size="small" 
                onDelete={() => removeFilter('toDate')} 
                color="primary" variant="outlined" 
              />
            )}
            {advancedFilters.inventoryType !== 'all' && (
              <Chip 
                label={`Type: ${advancedFilters.inventoryType}`} 
                size="small" 
                onDelete={() => removeFilter('inventoryType')} 
                color="primary" variant="outlined" 
              />
            )}
            {advancedFilters.associateId && (
              <Chip 
                label={`Associate: ${selectedFilterAssociate?.name || 'Selected'}`} 
                size="small" 
                onDelete={() => removeFilter('associateId')} 
                color="primary" variant="outlined" 
              />
            )}
            <Button size="small" variant="text" onClick={handleClearFilters} sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
              CLEAR ALL
            </Button>
          </Box>
        )}
      </Paper>

      {/* Table */}
      <Paper sx={{ borderRadius: 3, boxShadow: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Booking ID</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Customer</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Property Info</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Payment Plan</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Financial Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Request Info</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Status</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: 'text.primary' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {console.log('Table render - loading:', loading, 'error:', error, 'paginatedData length:', paginatedData?.length)}
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      Loading bookings...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                    <Button
                      variant="outlined"
                      startIcon={<Refresh />}
                      onClick={fetchBookings}
                    >
                      Retry
                    </Button>
                  </TableCell>
                </TableRow>
              ) : paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <Typography variant="body2" color="text.secondary">
                      No bookings found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((booking) => {
                  const progress = getPaymentProgress(booking.paidAmount, booking.plotAmount);
                  return (
                    <TableRow
                      key={booking.id}
                    >
                      <TableCell sx={{ cursor: 'default' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.875rem', cursor: 'default' }}>
                            {booking.bookingId.slice(-2)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="700" sx={{ cursor: 'default' }}>{booking.bookingId}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ cursor: 'default' }}>
                              <CalendarToday sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                              {booking.plotBookingDate}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main', fontSize: '0.875rem' }}>
                            {booking.customer.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="600" onClick={(e) => e.stopPropagation()}>
                              {booking.customer}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" onClick={(e) => e.stopPropagation()}>
                              Associate: {booking.associate}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="600">
                            <Home sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle', color: 'text.secondary' }} />
                            {booking.inventoryType || 'Plot'} {booking.plotNo}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            <Business sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                            {booking.branchName}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={booking.paymentPlan}
                          size="small"
                          variant="outlined"
                          color={booking.paymentPlan === 'EMI' ? 'primary' : 'secondary'}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>

                      <TableCell>
                        <Box sx={{ width: 200 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" fontWeight="600">
                              ₹{booking.paidAmount.toLocaleString('en-IN')}
                            </Typography>
                            <Typography variant="caption" fontWeight="600">
                              {progress.toFixed(0)}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={progress}
                            color="primary"
                            sx={{
                              height: 6,
                              borderRadius: 3,
                            }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            Total: ₹{booking.plotAmount.toLocaleString('en-IN')}
                            </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            By: {booking.requestedBy}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {booking.requestDate}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={booking.status.toUpperCase()}
                          size="small"
                          color="default"
                          sx={{ fontWeight: 700 }}
                        />
                      </TableCell>

                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          {/* <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleView(booking)}
                            sx={{
                              color: 'info.main',
                              '&:hover': {
                                backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                transform: 'scale(1.1)',
                              }
                            }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip> */}
                          {booking.status === 'pending' && canWriteBookingApproval && (
                            <>
                              <Tooltip title="Approve">
                                <IconButton
                                  size="small"
                                  onClick={() => handleApprove(booking)}
                                  sx={{
                                    color: 'text.secondary',
                                    '&:hover': {
                                      backgroundColor: 'rgba(54, 206, 59, 0.1)',
                                      transform: 'scale(1.1)',
                                    }
                                  }}
                                >
                                  <CheckCircle fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject">
                                <IconButton
                                  size="small"
                                  onClick={() => handleReject(booking)}
                                  sx={{
                                    color: 'text.secondary',
                                    '&:hover': {
                                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                      transform: 'scale(1.1)',
                                    }
                                  }}
                                >
                                  <Cancel fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {/* <Tooltip title="Print">
                          <IconButton 
                            size="small"
                            sx={{
                              color: 'text.secondary',
                              '&:hover': { 
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                transform: 'scale(1.1)',
                              }
                            }}
                          >
                            <Print fontSize="small" />
                          </IconButton>
                        </Tooltip> */}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ borderTop: '1px solid #e0e0e0' }}
        />
      </Paper>

      {/* View Booking Dialog (Read-Only with All Tabs) */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, height: '90vh' }
        }}
      >
        <DialogTitle sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: 3
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
              <Visibility sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight="bold">View Booking for Approval</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {selectedBooking?.bookingId} - {selectedBooking?.customer}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 4, maxHeight: 'calc(90vh - 240px)', overflow: 'auto' }}>
          {selectedBooking && (
            <Box>
              {/* Approval Status Alert */}
              {selectedBooking.status === 'pending' && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2" fontWeight="600">
                    This booking is pending approval. Please review all details before approving or rejecting.
                  </Typography>
                </Alert>
              )}
              {selectedBooking.status === 'approved' && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2" fontWeight="600">
                    Approved by {selectedBooking.approvedBy} on {selectedBooking.approvedDate}
                  </Typography>
                </Alert>
              )}
              {selectedBooking.status === 'rejected' && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2" fontWeight="600">
                    Rejected by {selectedBooking.rejectedBy} on {selectedBooking.rejectedDate}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Reason: {selectedBooking.rejectionReason}
                  </Typography>
                </Alert>
              )}

              {/* Booking Section */}
              <Box sx={{ mb: 4 }}>
                <Paper sx={{
                  p: 2,
                  mb: 3,
                  bgcolor: '#f5f5f5',
                  border: '1px solid #e0e0e0',
                  borderRadius: 2
                }}>
                  <Typography variant="h6" fontWeight="bold" color="text.primary">
                    Status: {selectedBooking.status.toUpperCase()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Booking Date: {selectedBooking.plotBookingDate}
                  </Typography>
                </Paper>

                <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                  Booking Information
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Booking ID" value={selectedBooking.bookingId} disabled />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Booking Date" type="date" defaultValue={selectedBooking.plotBookingDate} disabled InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Customer Name" defaultValue={selectedBooking.customer} disabled />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Associate/Agent" defaultValue={selectedBooking.associate} disabled />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Plot Number" defaultValue={selectedBooking.plotNo} disabled />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Branch Location" defaultValue={selectedBooking.branchName} disabled />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Payment Plan" defaultValue={selectedBooking.paymentPlan} disabled />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Status" defaultValue={selectedBooking.status} disabled />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Plot Amount" defaultValue={selectedBooking.plotAmount} disabled InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Discount Amount" defaultValue={selectedBooking.discountAmount} disabled InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Paid Amount" defaultValue={selectedBooking.paidAmount} disabled InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
                  </Grid>
                </Grid>




                {/* Financial Summary */}
                <Box sx={{ mt: 3 }}>
                  <Paper sx={{ p: 3, borderRadius: 2, bgcolor: 'white', border: '1px solid #e0e0e0' }}>
                    <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                      Financial Summary
                    </Typography>
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                      <Grid item xs={12} sm={2}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" fontWeight="bold" color="text.primary">
                            ₹{selectedBooking.plotAmount.toLocaleString('en-IN')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" fontWeight="bold" color="info.main">
                            ₹{selectedBooking.discountAmount.toLocaleString('en-IN')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">Discount</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={2.5}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" fontWeight="bold" color="warning.main">
                            ₹{selectedBooking.authorityCharge.toLocaleString('en-IN')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">Auth. Charge</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={2.5}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" fontWeight="bold" color="success.main">
                            ₹{selectedBooking.paidAmount.toLocaleString('en-IN')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">Paid Amount</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" fontWeight="bold" color="error.main">
                            ₹{(selectedBooking.plotAmount + selectedBooking.authorityCharge - selectedBooking.discountAmount - selectedBooking.paidAmount).toLocaleString('en-IN')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">Remaining</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Payment Progress */}
                  <Paper sx={{ p: 3, borderRadius: 2, bgcolor: 'white', border: '1px solid #e0e0e0', mt: 2 }}>
                    <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                      Payment Progress
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" fontWeight="600">
                          {getPaymentProgress(selectedBooking.paidAmount, selectedBooking.plotAmount).toFixed(1)}% Completed
                        </Typography>
                        <Typography variant="body2" fontWeight="600">
                          ₹{selectedBooking.paidAmount.toLocaleString('en-IN')} / ₹{selectedBooking.plotAmount.toLocaleString('en-IN')}
                        </Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={getPaymentProgress(selectedBooking.paidAmount, selectedBooking.plotAmount)} color="primary" sx={{ height: 12, borderRadius: 6 }} />
                    </Box>
                  </Paper>
                </Box>
              </Box>

              {/* Installment Section */}
              <Box sx={{ mb: 4 }}>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                  Installment Plan
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, bgcolor: 'white', border: '1px solid #f0f0f0', mb: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                          <TextField fullWidth label="Number of Installments" type="number" defaultValue="12" disabled />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField fullWidth label="Installment Amount" type="number" disabled InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField fullWidth label="Start Date" type="date" disabled InputLabelProps={{ shrink: true }} />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField fullWidth label="Frequency" defaultValue="Monthly" disabled />
                        </Grid>
                      </Grid>
                    </Paper>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#fafafa' }}>
                            <TableCell><strong>Sr No.</strong></TableCell>
                            <TableCell><strong>Due Date</strong></TableCell>
                            <TableCell><strong>Amount</strong></TableCell>
                            <TableCell><strong>Paid Date</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {[1, 2, 3].map((i) => (
                            <TableRow key={i}>
                              <TableCell>{i}</TableCell>
                              <TableCell>2025-{String(i).padStart(2, '0')}-01</TableCell>
                              <TableCell>₹40,000</TableCell>
                              <TableCell>{i === 1 ? '2025-01-01' : '-'}</TableCell>
                              <TableCell>
                                <Chip label={i === 1 ? 'PAID' : 'PENDING'} size="small" color="default" />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>
              </Box>

              {/* Receipt Section */}
              <Box sx={{ mb: 4 }}>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                  Receipt Management
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#fafafa' }}>
                            <TableCell><strong>Receipt No</strong></TableCell>
                            <TableCell><strong>Date</strong></TableCell>
                            <TableCell><strong>Amount</strong></TableCell>
                            <TableCell><strong>Payment Mode</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell><strong>Actions</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell>RCP001</TableCell>
                            <TableCell>2025-01-01</TableCell>
                            <TableCell>₹1,00,000</TableCell>
                            <TableCell>Cash</TableCell>
                            <TableCell><Chip label="CONFIRMED" size="small" color="default" /></TableCell>
                            <TableCell>
                              <IconButton size="small" color="primary"><Print fontSize="small" /></IconButton>
                              <IconButton size="small" color="info"><Download fontSize="small" /></IconButton>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>RCP002</TableCell>
                            <TableCell>2025-01-15</TableCell>
                            <TableCell>₹50,000</TableCell>
                            <TableCell>Bank Transfer</TableCell>
                            <TableCell><Chip label="PENDING" size="small" color="default" /></TableCell>
                            <TableCell>
                              <IconButton size="small" color="primary"><Print fontSize="small" /></IconButton>
                              <IconButton size="small" color="info"><Download fontSize="small" /></IconButton>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>
              </Box>

              {/* Document Section */}
              <Box sx={{ mb: 4 }}>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                  Document Management
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Uploaded Documents
                    </Typography>
                    <Grid container spacing={2}>
                      {['Aadhar Card', 'PAN Card', 'Agreement', 'Payment Receipt'].map((doc, idx) => (
                        <Grid item xs={12} md={6} key={idx}>
                          <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Description color="primary" />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" fontWeight="600">{doc}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Uploaded: 2025-01-{String(idx + 1).padStart(2, '0')}
                              </Typography>
                            </Box>
                            <IconButton size="small" color="primary">
                              <Download fontSize="small" />
                            </IconButton>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                </Grid>
              </Box>

              {/* Followup/Remark Section */}
              <Box sx={{ mb: 4 }}>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                  Followup & Remarks
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Previous Remarks
                    </Typography>
                    {['Customer called for payment update - Ready to pay next installment',
                      'Site visit scheduled for next week - Customer very satisfied with plot location',
                      'Documents submitted successfully - All verification completed'].map((remark, idx) => (
                        <Paper key={idx} sx={{ p: 2, mb: 2, bgcolor: 'white', border: '1px solid #f0f0f0' }}>
                          <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                            <Comment color="primary" />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2">{remark}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                By Admin - 2025-01-{String(idx + 1).padStart(2, '0')} at 10:{String(idx * 15).padStart(2, '0')} AM
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      ))}
                  </Grid>
                </Grid>
              </Box>

              {/* Commission Section */}
              <Box sx={{ mb: 4 }}>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                  Commission Details
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Commission Percentage" type="number" defaultValue="2.5" disabled InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Commission Amount" type="number" defaultValue="12500" InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} disabled />
                  </Grid>
                  <Grid item xs={12}>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#fafafa' }}>
                            <TableCell><strong>Agent</strong></TableCell>
                            <TableCell><strong>Role</strong></TableCell>
                            <TableCell><strong>Commission %</strong></TableCell>
                            <TableCell><strong>Amount</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell><strong>Paid Date</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell>{selectedBooking.associate}</TableCell>
                            <TableCell>Primary Agent</TableCell>
                            <TableCell>2.5%</TableCell>
                            <TableCell>₹12,500</TableCell>
                            <TableCell><Chip label="PAID" size="small" color="default" /></TableCell>
                            <TableCell>2025-01-05</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Manager B</TableCell>
                            <TableCell>Team Lead</TableCell>
                            <TableCell>1.0%</TableCell>
                            <TableCell>₹5,000</TableCell>
                            <TableCell><Chip label="PENDING" size="small" color="default" /></TableCell>
                            <TableCell>-</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>
              </Box>

              {/* Progress Section */}
              <Box sx={{ mb: 4 }}>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                  Booking Progress Timeline
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Box sx={{ position: 'relative', pl: 4 }}>
                  {[
                    { title: 'Booking Created', date: '2025-01-01 10:30 AM', status: 'completed', description: 'Initial booking registered in system' },
                    { title: 'Initial Payment Received', date: '2025-01-01 11:00 AM', status: 'completed', description: '₹1,00,000 received via Cash' },
                    { title: 'Document Verification', date: '2025-01-05 02:30 PM', status: 'completed', description: 'All documents verified and approved' },
                    { title: 'Plot Allocation', date: '2025-01-10 09:00 AM', status: 'in-progress', description: 'Plot P101 allocated, awaiting final approval' },
                    { title: 'Agreement Signing', date: 'TBD', status: 'pending', description: 'Pending completion of payment' },
                    { title: 'Registration', date: 'TBD', status: 'pending', description: 'Final registration pending' },
                  ].map((item, idx) => (
                    <Box key={idx} sx={{ mb: 3, position: 'relative' }}>
                      <Box sx={{ position: 'absolute', left: -20, top: 0, width: 16, height: 16, borderRadius: '50%', bgcolor: item.status === 'completed' ? '#536DFE' : 'grey.400', border: '3px solid white', boxShadow: 2 }} />
                      {idx < 5 && <Box sx={{ position: 'absolute', left: -13, top: 16, width: 2, height: 50, bgcolor: item.status === 'completed' ? '#536DFE' : 'grey.300' }} />}
                      <Paper sx={{ p: 2, bgcolor: 'white', border: '1px solid #f0f0f0' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="body1" fontWeight="600">{item.title}</Typography>
                          {item.status === 'completed' && <CheckCircleOutline color="primary" fontSize="small" />}
                          {item.status === 'in-progress' && <WatchLater color="action" fontSize="small" />}
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block">{item.date}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{item.description}</Typography>
                      </Paper>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Booking Status Section */}
              <Box sx={{ mb: 4 }}>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                  Booking Status Management
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Current Status" defaultValue={selectedBooking.status} disabled />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Status Change Date" type="date" defaultValue={new Date().toISOString().split('T')[0]} InputLabelProps={{ shrink: true }} disabled />
                  </Grid>
                  <Grid item xs={12}>
                    <Paper sx={{ p: 3, bgcolor: 'white', border: '1px solid #e0e0e0' }}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Status History
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="body2"><strong>Initial</strong> → Changed to <strong>Pending</strong></Typography>
                            <Typography variant="caption" color="text.secondary">Reason: New booking created, awaiting approval</Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">2025-01-01 10:30 AM</Typography>
                        </Box>
                        {selectedBooking.status === 'approved' && (
                          <>
                            <Divider />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box>
                                <Typography variant="body2"><strong>Pending</strong> → Changed to <strong>Approved</strong></Typography>
                                <Typography variant="caption" color="text.secondary">Reason: All documents verified and approved by {selectedBooking.approvedBy}</Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary">{selectedBooking.approvedDate}</Typography>
                            </Box>
                          </>
                        )}
                        {selectedBooking.status === 'rejected' && (
                          <>
                            <Divider />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box>
                                <Typography variant="body2"><strong>Pending</strong> → Changed to <strong>Rejected</strong></Typography>
                                <Typography variant="caption" color="text.secondary">Reason: {selectedBooking.rejectionReason}</Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary">{selectedBooking.rejectedDate}</Typography>
                            </Box>
                          </>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button
            onClick={handleCloseViewDialog}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              borderRadius: 2
            }}
          >
            Close
          </Button>
          {selectedBooking?.status === 'pending' && (
            <>
              <Button
                variant="outlined"
                startIcon={<ThumbDown />}
                onClick={() => {
                  handleCloseViewDialog();
                  handleReject(selectedBooking);
                }}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 4,
                  borderRadius: 2,
                }}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                startIcon={<CheckCircle />}
                onClick={() => {
                  handleCloseViewDialog();
                  handleApprove(selectedBooking);
                }}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 4,
                  borderRadius: 2,
                }}
              >
                Approve
              </Button>
            </>
          )}
          <Button
            variant="contained"
            startIcon={<Print />}
            color="primary"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              borderRadius: 2,
            }}
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>



      {/* Approval Dialog */}
      <Dialog
        open={approvalDialogOpen}
        onClose={handleCloseApprovalDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{
          bgcolor: actionType === 'approve' ? 'success.main' : 'error.main',
          color: 'white',
          py: 3
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
              {actionType === 'approve' ? (
                <ThumbUp sx={{ fontSize: 28 }} />
              ) : (
                <ThumbDown sx={{ fontSize: 28 }} />
              )}
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {actionType === 'approve' ? 'Approve Booking' : 'Reject Booking'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {selectedBooking?.bookingId} - {selectedBooking?.customer}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 4 }}>
          {selectedBooking && (
            <Box sx={{ mt: 2 }}>
              {/* Today's Date Display */}
              {/* <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CalendarToday color="primary" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {actionType === 'approve' ? 'Approval' : 'Rejection'} Date
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {new Date().toLocaleDateString('en-GB', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </Typography>
                  </Box>
                </Box>
              </Paper> */}

              {/* <LocalizationProvider dateAdapter={AdapterMoment}>
                <Paper
                  sx={{
                    p: 2,
                    mb: 3,
                    bgcolor: '#f5f5f5',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CalendarToday color="primary" />

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {actionType === 'approve' ? 'Approval' : 'Rejection'} Date
                      </Typography>

                      <DatePicker
                        value={moment(approvalDate)}
                        onChange={(newValue) => {
                          if (newValue) {
                            setApprovalDate(newValue.format('YYYY-MM-DD'));
                          }
                        }}
                        inputFormat="DD/MM/YYYY"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="standard"
                            InputProps={{
                              ...params.InputProps,
                              disableUnderline: true,
                              sx: {
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                color: 'primary.main'
                              }
                            }}
                          />
                        )}
                      />
                    </Box>
                  </Box>
                </Paper>
              </LocalizationProvider> */}

              <LocalizationProvider dateAdapter={AdapterMoment}>
                <Paper
                  sx={{
                    p: 2,
                    mb: 3,
                    bgcolor: '#f9fafb',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>

                    <Box sx={{ minWidth: 50 }}>

                      <Typography variant="body2" color="text.secondary">
                        {actionType === 'approve' ? 'Approval' : 'Rejection'} Date
                      </Typography>

                      <DatePicker
                        value={approvalDate ? moment(approvalDate) : null}
                        onChange={(newValue) => {
                          if (newValue) {
                            setApprovalDate(newValue.format('YYYY-MM-DD'));
                          }
                        }}
                        inputFormat="DD/MM/YYYY"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            variant="outlined"
                            fullWidth
                          />
                        )}
                      />
                    </Box>
                  </Box>
                </Paper>
              </LocalizationProvider>

              {/* Booking Details */}
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom sx={{ mb: 2 }}>
                Booking Details
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Booking ID"
                    value={selectedBooking.bookingId}
                    disabled
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Booking Date"
                    value={selectedBooking.plotBookingDate}
                    disabled
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Customer Name"
                    value={selectedBooking.customer}
                    disabled
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Associate/Agent"
                    value={selectedBooking.associate}
                    disabled
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Plot Number"
                    value={selectedBooking.plotNo}
                    disabled
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Branch Location"
                    value={selectedBooking.branchName}
                    disabled
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Payment Plan"
                    value={selectedBooking.paymentPlan}
                    disabled
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Status"
                    value={selectedBooking.status.toUpperCase()}
                    disabled
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Plot Amount"
                    value={`₹${selectedBooking.plotAmount.toLocaleString('en-IN')}`}
                    disabled
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Discount Amount"
                    value={`₹${selectedBooking.discountAmount.toLocaleString('en-IN')}`}
                    disabled
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Authority Charge"
                    value={`₹${selectedBooking.authorityCharge.toLocaleString('en-IN')}`}
                    disabled
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Paid Amount"
                    value={`₹${selectedBooking.paidAmount.toLocaleString('en-IN')}`}
                    disabled
                    sx={{ mb: 2 }}
                  />
                </Grid>
              </Grid>

              {/* Additional Applicants Section (table layout) */}
              {(() => {
                const additional = selectedBooking?.additionalCustomers || selectedBooking?._original?.additionalCustomers || [];
                console.log('BookingApproval - Additional customers:', additional, 'length:', additional.length);
                console.log('selectedBooking keys:', Object.keys(selectedBooking || {}));
                console.log('selectedBooking._original keys:', Object.keys(selectedBooking?._original || {}));
                if (Array.isArray(additional) && additional.length > 0) {
                  console.log('Rendering additional applicants section');
                  return (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                        Additional Applicants
                      </Typography>
                      <Paper sx={{ borderRadius: 2, mb: 2 }}>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell><Typography variant="subtitle2">Name</Typography></TableCell>
                                <TableCell><Typography variant="subtitle2">Mobile</Typography></TableCell>
                                <TableCell><Typography variant="subtitle2">Alternate</Typography></TableCell>
                                <TableCell><Typography variant="subtitle2">Relation</Typography></TableCell>
                                <TableCell><Typography variant="subtitle2">Email</Typography></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {additional.map((c, i) => (
                                <TableRow key={`additional-${i}`}>
                                  <TableCell>{c.name || '-'}</TableCell>
                                  <TableCell>{c.mobile || '-'}</TableCell>
                                  <TableCell>{c.alternateMobile || '-'}</TableCell>
                                  <TableCell>{c.relation || '-'}</TableCell>
                                  <TableCell>{c.email || '-'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    </Box>
                  );
                }
                return null;
              })()}

              {/* Financial Summary */}
              <Paper sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: 'white', border: '1px solid #e0e0e0' }}>
                <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                  Financial Summary
                </Typography>
                <Grid container spacing={3} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={2.4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h5" fontWeight="bold" color="text.primary">
                        ₹{selectedBooking.plotAmount.toLocaleString('en-IN')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Plot Amount</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={2.4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h5" fontWeight="bold" color="info.main">
                        ₹{selectedBooking.discountAmount.toLocaleString('en-IN')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Discount</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={2.4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h5" fontWeight="bold" color="warning.main">
                        ₹{selectedBooking.authorityCharge.toLocaleString('en-IN')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Auth. Charge</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={2.4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h5" fontWeight="bold" color="success.main">
                        ₹{selectedBooking.paidAmount.toLocaleString('en-IN')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Paid Amount</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={2.4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h5" fontWeight="bold" color="error.main">
                        ₹{(selectedBooking.plotAmount + selectedBooking.authorityCharge - selectedBooking.discountAmount - selectedBooking.paidAmount).toLocaleString('en-IN')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Remaining</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Payment Progress */}
              <Paper sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: 'white', border: '1px solid #e0e0e0' }}>
                <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                  Payment Progress
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" fontWeight="600">
                      {getPaymentProgress(selectedBooking.paidAmount, selectedBooking.plotAmount).toFixed(1)}% Completed
                    </Typography>
                    <Typography variant="body2" fontWeight="600">
                      ₹{selectedBooking.paidAmount.toLocaleString('en-IN')} / ₹{selectedBooking.plotAmount.toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={getPaymentProgress(selectedBooking.paidAmount, selectedBooking.plotAmount)}
                    color="primary"
                    sx={{ height: 12, borderRadius: 6 }}
                  />
                </Box>
              </Paper>

              {/* Remark Field */}
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                {actionType === 'approve' ? 'Approval' : 'Rejection'} Remark <span style={{ color: 'text.secondary', fontSize: '0.875rem' }}>(Optional)</span>
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder={actionType === 'approve' ? 'Enter your approval remark here (optional)...' : 'Enter your rejection reason here (optional)...'}
                value={approvalRemark}
                onChange={(e) => setApprovalRemark(e.target.value)}
                sx={{ mt: 2 }}
              />

              {/* Error Message */}
              {error && (
                <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button
            onClick={handleCloseApprovalDialog}
            variant="outlined"
            disabled={approvalLoading}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              borderRadius: 2
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color={actionType === 'approve' ? 'success' : 'error'}
            startIcon={actionType === 'approve' ? <ThumbUp /> : <ThumbDown />}
            onClick={handleSubmitApproval}
            disabled={approvalLoading}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              borderRadius: 2,
            }}
          >
            {approvalLoading
              ? (actionType === 'approve' ? 'Approving...' : 'Rejecting...')
              : (actionType === 'approve' ? 'Approve Booking' : 'Reject Booking')
            }
          </Button>
        </DialogActions>
      </Dialog>
      <AlertModal open={alertModal.open} title={alertModal.title} message={alertModal.message} onClose={closeAlert} />
    </Box>
  );
};

export default BookingApproval;

